// src/views/admin/users/chefs/view/tabs/PayrollTab.jsx
import React, { useEffect, useState } from 'react';
import Card from '@/components/ui/cards/Card';
import ToastMe from '@/components/ui/ToastMe';
import { API_BASE_URL } from '@/constants/ApiConstant';

const API_BASE = API_BASE_URL;

export default function PayrollTab({ employeeId }) {
  const [payrollData, setPayrollData] = useState(null);
  const [loading, setLoading] = useState(false);
  const [month, setMonth] = useState(new Date().getMonth()); // 0-based (0 = Jan)
  const [year, setYear] = useState(new Date().getFullYear());

  const months = [
    'January', 'February', 'March', 'April', 'May', 'June',
    'July', 'August', 'September', 'October', 'November', 'December'
  ];

  /** Build the Authorization header from localStorage */
  const authHdr = () => {
    const tok =
      localStorage.getItem('jwt_token')  ||
      localStorage.getItem('token')      ||
      localStorage.getItem('adminToken') ||
      '';
    const companyId = localStorage.getItem('company_id');
    const branchId = localStorage.getItem('branch_id');
    
    const headers = {
      'Content-Type': 'application/json',
    };
    
    if (tok) {
      headers['Authorization'] = `Bearer ${tok}`;
    }
    if (companyId) {
      headers['X-Company-Id'] = companyId;
    }
    if (branchId && branchId !== 'null' && branchId !== '') {
      headers['X-Branch-Id'] = branchId;
    }
    
    return headers;
  };

  const calculatePayroll = async () => {
    if (!employeeId) return;
    
    setLoading(true);
    try {
      const url = `${API_BASE}/payroll/calculate`;
      console.log('[PayrollTab] POST', url, { employeeId, month, year });
      
      const res = await fetch(url, {
        method: 'POST',
        headers: authHdr(),
        body: JSON.stringify({ employeeId, month, year }),
      });

      if (!res.ok) {
        const errorJson = await res.json().catch(() => ({}));
        const errorMsg = errorJson.message || `Server replied ${res.status}`;
        ToastMe(errorMsg, 'error');
        console.error('[PayrollTab] Server error:', errorJson);
        throw new Error(errorMsg);
      }

      const json = await res.json();
      if (json.success) {
        setPayrollData(json.data);
      } else {
        ToastMe(json.message || 'Failed to calculate payroll', 'error');
      }
    } catch (err) {
      const errorMsg = err.message || 'Error calculating payroll';
      ToastMe(errorMsg, 'error');
      console.error('[PayrollTab] Error:', err);
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    if (employeeId) {
      calculatePayroll();
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [employeeId, month, year]);

  if (!employeeId) {
    return <Card className="p-6 text-center">No employee ID provided.</Card>;
  }

  return (
    <Card className="p-6">
      <div className="mb-6">
        <h3 className="text-xl font-semibold mb-4">Payroll Calculation</h3>
        
        {/* Month/Year Selector */}
        <div className="flex gap-4 items-center mb-6">
          <div>
            <label className="block text-sm font-medium text-gray-700 mb-1">
              Month
            </label>
            <select
              value={month}
              onChange={(e) => setMonth(Number(e.target.value))}
              className="px-3 py-2 border border-gray-300 rounded-md focus:outline-none focus:ring-2 focus:ring-blue-500"
            >
              {months.map((m, i) => (
                <option key={i} value={i}>
                  {m}
                </option>
              ))}
            </select>
          </div>
          
          <div>
            <label className="block text-sm font-medium text-gray-700 mb-1">
              Year
            </label>
            <input
              type="number"
              value={year}
              onChange={(e) => setYear(Number(e.target.value))}
              className="px-3 py-2 border border-gray-300 rounded-md focus:outline-none focus:ring-2 focus:ring-blue-500"
              min="2020"
              max="2100"
            />
          </div>
          
          <button
            onClick={calculatePayroll}
            disabled={loading}
            className="mt-6 px-4 py-2 bg-blue-600 text-white rounded-md hover:bg-blue-700 disabled:opacity-50"
          >
            {loading ? 'Calculating...' : 'Calculate'}
          </button>
        </div>
      </div>

      {loading && (
        <div className="text-center py-8">Calculating payroll...</div>
      )}

      {!loading && payrollData && (
        <div className="space-y-6">
          {/* Period Information */}
          <div className="bg-gray-50 p-4 rounded-lg">
            <h4 className="font-semibold mb-2">Period Information</h4>
            <div className="grid grid-cols-2 gap-4 text-sm">
              <div>
                <span className="text-gray-600">From:</span>{' '}
                {new Date(payrollData.period.from).toLocaleDateString()}
              </div>
              <div>
                <span className="text-gray-600">To:</span>{' '}
                {new Date(payrollData.period.to).toLocaleDateString()}
              </div>
              <div>
                <span className="text-gray-600">Working Days:</span>{' '}
                {payrollData.period.workingDays}
              </div>
              <div>
                <span className="text-gray-600">Calendar Days:</span>{' '}
                {payrollData.period.calendarDays}
              </div>
            </div>
          </div>

          {/* Attendance Summary */}
          <div className="bg-blue-50 p-4 rounded-lg">
            <h4 className="font-semibold mb-2">Attendance Summary</h4>
            <div className="grid grid-cols-3 gap-4 text-sm">
              <div>
                <span className="text-gray-600">Days Present:</span>{' '}
                <span className="font-semibold">{payrollData.attendance.daysPresent}</span>
              </div>
              <div>
                <span className="text-gray-600">Days Late:</span>{' '}
                <span className="font-semibold text-orange-600">{payrollData.attendance.daysLate}</span>
              </div>
              <div>
                <span className="text-gray-600">Days Absent:</span>{' '}
                <span className="font-semibold text-red-600">{payrollData.attendance.daysAbsent}</span>
              </div>
              <div>
                <span className="text-gray-600">Days Leave:</span>{' '}
                <span className="font-semibold">{payrollData.attendance.daysLeave}</span>
              </div>
              <div>
                <span className="text-gray-600">Half Days:</span>{' '}
                <span className="font-semibold">{payrollData.attendance.daysHalfDay}</span>
              </div>
              <div>
                <span className="text-gray-600">Overtime (hours):</span>{' '}
                <span className="font-semibold text-green-600">
                  {(payrollData.attendance.overtimeMinutes / 60).toFixed(1)}
                </span>
              </div>
            </div>
          </div>

          {/* Salary Breakdown */}
          <div className="bg-green-50 p-4 rounded-lg">
            <h4 className="font-semibold mb-2">Salary Breakdown</h4>
            <div className="grid grid-cols-2 gap-4 text-sm">
              <div>
                <span className="text-gray-600">Gross Salary:</span>{' '}
                <span className="font-semibold">${payrollData.salary.gross.toFixed(2)}</span>
              </div>
              <div>
                <span className="text-gray-600">Daily Rate:</span>{' '}
                <span className="font-semibold">${payrollData.salary.dailyRate.toFixed(2)}</span>
              </div>
              <div>
                <span className="text-gray-600">Hourly Rate:</span>{' '}
                <span className="font-semibold">${payrollData.salary.hourlyRate.toFixed(2)}</span>
              </div>
              <div>
                <span className="text-gray-600">Pro-rated Gross:</span>{' '}
                <span className="font-semibold">${payrollData.salary.proRatedGross.toFixed(2)}</span>
              </div>
            </div>
          </div>

          {/* Deductions */}
          <div className="bg-red-50 p-4 rounded-lg">
            <h4 className="font-semibold mb-2">Deductions</h4>
            <div className="grid grid-cols-2 gap-4 text-sm">
              <div>
                <span className="text-gray-600">Late Penalty:</span>{' '}
                <span className="font-semibold text-red-600">
                  ${payrollData.deductions.latePenalty.toFixed(2)}
                </span>
              </div>
              <div>
                <span className="text-gray-600">Absent Penalty:</span>{' '}
                <span className="font-semibold text-red-600">
                  ${payrollData.deductions.absentPenalty.toFixed(2)}
                </span>
              </div>
              <div>
                <span className="text-gray-600">Half Day Penalty:</span>{' '}
                <span className="font-semibold text-red-600">
                  ${payrollData.deductions.halfDayPenalty.toFixed(2)}
                </span>
              </div>
              <div>
                <span className="text-gray-600">Total Deductions:</span>{' '}
                <span className="font-semibold text-red-600">
                  ${payrollData.deductions.totalPenalties.toFixed(2)}
                </span>
              </div>
            </div>
          </div>

          {/* Additions */}
          <div className="bg-yellow-50 p-4 rounded-lg">
            <h4 className="font-semibold mb-2">Additions</h4>
            <div className="grid grid-cols-2 gap-4 text-sm">
              <div>
                <span className="text-gray-600">Overtime:</span>{' '}
                <span className="font-semibold text-green-600">
                  ${payrollData.additions.overtime.toFixed(2)}
                </span>
              </div>
              <div>
                <span className="text-gray-600">Bonuses:</span>{' '}
                <span className="font-semibold text-green-600">
                  ${payrollData.additions.bonuses.toFixed(2)}
                </span>
              </div>
              <div>
                <span className="text-gray-600">Allowances:</span>{' '}
                <span className="font-semibold text-green-600">
                  ${payrollData.additions.allowances.toFixed(2)}
                </span>
              </div>
              <div>
                <span className="text-gray-600">Total Additions:</span>{' '}
                <span className="font-semibold text-green-600">
                  ${payrollData.additions.totalAdditions.toFixed(2)}
                </span>
              </div>
            </div>
          </div>

          {/* Taxes */}
          <div className="bg-purple-50 p-4 rounded-lg">
            <h4 className="font-semibold mb-2">Taxes</h4>
            <div className="grid grid-cols-2 gap-4 text-sm">
              <div>
                <span className="text-gray-600">Income Tax:</span>{' '}
                <span className="font-semibold">${payrollData.taxes.incomeTax.toFixed(2)}</span>
              </div>
              <div>
                <span className="text-gray-600">Social Security:</span>{' '}
                <span className="font-semibold">${payrollData.taxes.socialSecurity.toFixed(2)}</span>
              </div>
              <div>
                <span className="text-gray-600">Other Taxes:</span>{' '}
                <span className="font-semibold">${payrollData.taxes.otherTaxes.toFixed(2)}</span>
              </div>
              <div>
                <span className="text-gray-600">Total Taxes:</span>{' '}
                <span className="font-semibold">${payrollData.taxes.totalTaxes.toFixed(2)}</span>
              </div>
            </div>
          </div>

          {/* Final Summary */}
          <div className="bg-blue-600 text-white p-6 rounded-lg">
            <h4 className="font-semibold text-lg mb-4">Final Summary</h4>
            <div className="grid grid-cols-2 gap-4 text-sm">
              <div>
                <span>Gross Salary:</span>{' '}
                <span className="font-semibold">${payrollData.summary.gross.toFixed(2)}</span>
              </div>
              <div>
                <span>Deductions:</span>{' '}
                <span className="font-semibold">${payrollData.summary.deductions.toFixed(2)}</span>
              </div>
              <div>
                <span>Additions:</span>{' '}
                <span className="font-semibold text-green-300">
                  +${payrollData.additions.totalAdditions.toFixed(2)}
                </span>
              </div>
              <div>
                <span>Taxes:</span>{' '}
                <span className="font-semibold">${payrollData.summary.taxes.toFixed(2)}</span>
              </div>
              <div className="col-span-2 pt-4 border-t border-blue-400">
                <div className="flex justify-between items-center">
                  <span className="text-lg font-semibold">Net Salary:</span>
                  <span className="text-2xl font-bold">
                    ${payrollData.summary.net.toFixed(2)}
                  </span>
                </div>
              </div>
            </div>
          </div>
        </div>
      )}

      {!loading && !payrollData && (
        <div className="text-center py-8 text-gray-500">
          Select a month and year, then click Calculate to view payroll details.
        </div>
      )}
    </Card>
  );
}

